import React, { useState, useEffect } from 'react';
import { BackofficeLayout } from '../../layouts/BackofficeLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Send, Check, X, Eye, CheckCheck, AlertTriangle } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

const STATUS_COLORS = {
  draft: 'text-slate-400 border-slate-600',
  pending_approval: 'text-amber-400 border-amber-500/30 bg-amber-500/10',
  approved: 'text-green-400 border-green-500/30 bg-green-500/10',
  sent: 'text-sky-400 border-sky-500/30',
  failed: 'text-red-400 border-red-500/30',
  cancelled: 'text-slate-500 border-slate-700',
};

export default function BackofficeOutbox() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending_approval');
  const [preview, setPreview] = useState(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [sendingConfig, setSendingConfig] = useState(null);

  const load = (status) => {
    setLoading(true);
    api.get('/backoffice/outbox', { params: { status: status || undefined, limit: 100 } })
      .then(r => { setMessages(r.data.messages || []); setTotal(r.data.total); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(filter);
    api.get('/backoffice/sending-config').then(r => setSendingConfig(r.data)).catch(() => {});
  }, [filter]);

  const approve = async (id) => {
    await api.post(`/backoffice/outbox/${id}/approve`);
    toast.success('Approved');
    load(filter);
    setPreview(null);
  };

  const reject = async (id) => {
    await api.post(`/backoffice/outbox/${id}/reject`);
    toast.success('Rejected');
    load(filter);
    setPreview(null);
  };

  const bulkApprove = async () => {
    const r = await api.post('/backoffice/outbox/bulk-approve');
    toast.success(`Approved ${r.data.approved} messages`);
    load(filter);
  };

  const saveEdit = async () => {
    await api.patch(`/backoffice/outbox/${preview.id}`, { subject: editSubject, body: editBody });
    toast.success('Updated');
    load(filter);
    setPreview(null);
  };

  const openPreview = (m) => {
    setPreview(m);
    setEditSubject(m.subject);
    setEditBody(m.body);
  };

  const pendingCount = messages.filter(m => m.status === 'pending_approval').length;

  return (
    <BackofficeLayout>
      <div data-testid="backoffice-outbox">
        {sendingConfig && !sendingConfig.outbound_sending_enabled && (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/10 border border-amber-500/20 rounded-sm mb-4" data-testid="sending-disabled-banner">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <span className="text-xs text-amber-300">Sending Disabled</span>
            <span className="text-xs text-slate-500">Approved messages will queue but not send until outbound is enabled.</span>
          </div>
        )}
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            OUTBOX <span className="text-slate-500 font-mono text-sm ml-2">{total}</span>
          </h1>
          <div className="flex gap-2">
            {filter === 'pending_approval' && pendingCount > 0 && (
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-sm" onClick={bulkApprove} data-testid="bulk-approve-btn">
                <CheckCheck className="w-4 h-4 mr-1" /> Approve All ({pendingCount})
              </Button>
            )}
          </div>
        </div>

        <div className="flex gap-1 mb-4">
          {['pending_approval', 'approved', 'sent', 'cancelled', ''].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-xs rounded-sm transition-colors ${filter === s ? 'bg-slate-700 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
              data-testid={`filter-${s || 'all'}`}
            >
              {s ? s.replace('_', ' ') : 'all'}
            </button>
          ))}
        </div>

        <div className="space-y-1">
          {loading ? (
            [...Array(5)].map((_, i) => <div key={i} className="h-14 bg-slate-900 border border-slate-800 rounded-sm animate-pulse" />)
          ) : messages.length === 0 ? (
            <p className="text-slate-500 text-sm py-8 text-center">No messages</p>
          ) : (
            messages.map(m => (
              <div key={m.id} className="flex items-center justify-between px-4 py-3 bg-slate-900 border border-slate-800 rounded-sm">
                <button onClick={() => openPreview(m)} className="flex items-center gap-3 min-w-0 text-left flex-1" data-testid={`outbox-row-${m.id}`}>
                  <Send className="w-4 h-4 text-slate-600 flex-shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-200 truncate">{m.prospect_name || m.to_email}</p>
                    <p className="text-xs text-slate-500 truncate">{m.subject}</p>
                  </div>
                </button>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[m.status] || ''}`}>{m.status.replace('_', ' ')}</Badge>
                  {m.provider_status && m.provider_status !== m.status && (
                    <Badge variant="outline" className={`text-[10px] ${
                      m.provider_status === 'delivered' ? 'text-green-400 border-green-500/30' :
                      m.provider_status === 'bounced' ? 'text-red-400 border-red-500/30' :
                      m.provider_status === 'complained' ? 'text-red-400 border-red-500/30' :
                      'text-slate-400 border-slate-600'
                    }`} data-testid={`provider-status-${m.id}`}>{m.provider_status}</Badge>
                  )}
                  {m.status === 'pending_approval' && (
                    <>
                      <Button variant="ghost" size="sm" className="text-green-400 hover:text-green-300 h-7 w-7 p-0" onClick={() => approve(m.id)} data-testid={`approve-${m.id}`}>
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 h-7 w-7 p-0" onClick={() => reject(m.id)} data-testid={`reject-${m.id}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-300 h-7 w-7 p-0" onClick={() => openPreview(m)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Preview / Edit Drawer */}
        <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
          <DialogContent className="bg-slate-900 border-slate-700 text-slate-200 max-w-lg">
            {preview && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-2">
                    <Send className="w-4 h-4" /> {preview.prospect_name || preview.to_email}
                  </DialogTitle>
                  <DialogDescription className="text-slate-400 text-sm">Review and approve outreach message.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                    <span>To: {preview.to_email}</span>
                    <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[preview.status]}`}>{preview.status.replace('_', ' ')}</Badge>
                    {preview.provider_status && (
                      <Badge variant="outline" className={`text-[10px] ${
                        preview.provider_status === 'delivered' ? 'text-green-400 border-green-500/30' :
                        preview.provider_status === 'bounced' ? 'text-red-400 border-red-500/30' :
                        preview.provider_status === 'complained' ? 'text-red-400 border-red-500/30' :
                        'text-slate-400 border-slate-600'
                      }`}>{preview.provider_status}</Badge>
                    )}
                    {preview.provider && <span className="text-slate-600">via {preview.provider}</span>}
                    {preview.step_number && <span>Step {preview.step_number}</span>}
                  </div>

                  {/* Compliance hints */}
                  <div className="flex flex-wrap gap-1.5">
                    {preview.body?.includes('unsubscribe') && (
                      <Badge variant="outline" className="text-[9px] text-green-400 border-green-500/30">Opt-out included</Badge>
                    )}
                    {preview.to_email?.includes('.co.uk') && (
                      <Badge variant="outline" className="text-[9px] text-blue-400 border-blue-500/30">Corporate email</Badge>
                    )}
                  </div>

                  {preview.status === 'pending_approval' || preview.status === 'draft' ? (
                    <>
                      <div><Label className="text-slate-400 text-xs">Subject</Label>
                        <Input className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" value={editSubject} onChange={e => setEditSubject(e.target.value)} />
                      </div>
                      <div><Label className="text-slate-400 text-xs">Body</Label>
                        <Textarea className="bg-slate-800 border-slate-700 text-slate-200 rounded-sm mt-1 min-h-[200px] font-mono text-xs" value={editBody} onChange={e => setEditBody(e.target.value)} />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-slate-800 p-3 rounded-sm">
                        <p className="text-xs font-semibold text-slate-300 mb-1">{preview.subject}</p>
                        <pre className="text-xs text-slate-400 whitespace-pre-wrap font-sans">{preview.body}</pre>
                      </div>
                    </>
                  )}
                </div>
                <DialogFooter className="flex gap-2">
                  {(preview.status === 'pending_approval' || preview.status === 'draft') && (
                    <>
                      <Button variant="ghost" className="text-slate-400" onClick={() => { saveEdit(); }}>Save Changes</Button>
                      <Button variant="ghost" className="text-red-400" onClick={() => reject(preview.id)}>Reject</Button>
                      <Button className="bg-green-600 hover:bg-green-700 text-white rounded-sm" onClick={() => approve(preview.id)} data-testid="preview-approve-btn">
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                    </>
                  )}
                  {preview.status !== 'pending_approval' && preview.status !== 'draft' && (
                    <Button variant="ghost" className="text-slate-400" onClick={() => setPreview(null)}>Close</Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </BackofficeLayout>
  );
}
